/**
 * Guarded access to the frames R2 currently has attached.
 *
 * The navigator hands out live frame objects whose `iframe` and `window`
 * members are getters that throw once the pool has destroyed the
 * frame ("Trying to use frame when it doesn't exist"). Optional
 * chaining is no defence, since `frame?.iframe` still
 * evaluates the getter. The pool destroys and recreates
 * frames on every page turn and chapter change, so
 * code that walks it outside R2's own callbacks
 * races with that teardown.
 */

/** The navigator member every read here goes through. */
export const FRAMES_MEMBER = '_cframes';

/**
 * The frames the navigator currently holds, holes dropped.
 * @param {object} nav - The EpubNavigator instance
 * @returns {object[]} The frames present right now, possibly empty
 */
export function attachedFrames(nav) {
    return attachedFrameSlots(nav).filter(Boolean);
}

/**
 * The frame slots the navigator currently holds, with undefined holes preserved.
 * In fixed-layout spreads, the pool indexes frames by reading order position,
 * leaving undefined slots for pages not yet loaded. Callers that pair frames
 * with `viewport.readingOrder` by index need these holes kept so indices stay aligned —
 * without them, a missing left page would shift the right page's frame one position
 * left, misaligning the key-to-rect pairing.
 *
 * `_cframes` is R2's public convenience over `framePool.currentFrames`, not a
 * second collection, and it already discards the destroyed reflowable frames.
 * It still throws on FXL, where `currentFrames` indexes the reading order by
 * slide with no range guard.
 * @param {object} nav - The EpubNavigator instance
 * @returns {(object|undefined)[]} The frame slots right now, undefined holes included
 */
export function attachedFrameSlots(nav) {
    try {
        // eslint-disable-next-line no-restricted-syntax -- the one sanctioned read
        return nav?._cframes ?? [];
    } catch {
        // The pool is mid-update. The next call sees the settled frames.
        return [];
    }
}

/**
 * Points a frame's comms at `listener`.
 * @param {object} frame - A frame from attachedFrames()
 * @param {(key: string, value: unknown) => void} listener
 * @returns {boolean} whether the frame had comms to take it
 */
export function listenToFrame(frame, listener) {
    const comms = frame?.msg;
    if (!comms) return false;
    try {
        comms.listener = listener;
        return true;
    } catch {
        // The setter delivers the buffered messages, so one that throws must not
        // cost the frames still waiting for a listener.
        return false;
    }
}

/**
 * Sends one command to a frame and resolves with its acknowledgement.
 * @param {object} frame - A frame from attachedFrames()
 * @param {string} command - A command the frame's modules registered
 * @param {unknown} data - The command's payload
 * @returns {Promise<boolean>} the frame's acknowledgement, false when there is
 * no comms to send over. Halted comms take the command and never
 * answer, so callers impose their own timeout.
 */
export function sendToFrame(frame, command, data) {
    const comms = frame?.msg;
    if (!comms) return Promise.resolve(false);
    return new Promise(resolve => {
        try {
            comms.send(command, data, ok => resolve(!!ok));
        } catch {
            // send() posts into the frame's window, which a destroyed frame no
            // longer has. Guarding the promise instead would reject, not answer.
            resolve(false);
        }
    });
}

/** @returns {Document|null} the document of an R2 frame, or null when the frame is gone */
export function readFrameDocument(frame) {
    try {
        return readFrameWindow(frame)?.document ?? null;
    } catch {
        // The browsing context went away between the window read and this one.
        return null;
    }
}

/**
 * The content window of an R2 frame, or null when the frame is already gone.
 * Every R2 frame class answers `window` (with the window or a throw), so
 * that read settles the result for all of them. The `iframe` leg
 * covers a frame object exposing no `window` at all.
 * @param {object} frame - A frame from attachedFrames()
 * @returns {Window|null}
 */
export function readFrameWindow(frame) {
    try {
        return frame?.window ?? frame?.iframe?.contentWindow ?? null;
    } catch {
        // Frame destroyed or not attached yet.
        return null;
    }
}

/**
 * The iframe element of an R2 frame, or null when the frame is already gone.
 * Optional chaining is no defence here: `frame?.iframe` still evaluates a
 * getter that throws once the pool has destroyed the frame.
 * @param {object|undefined} frame - A slot from attachedFrameSlots(), which may be empty
 * @returns {HTMLIFrameElement|null}
 */
export function readFrameIframe(frame) {
    try {
        return frame?.iframe ?? frame?.window?.frameElement ?? null;
    } catch {
        return null;
    }
}

/**
 * The viewport the navigator reports, or null when the pool is mid-update.
 * On fixed layout the getter walks the reading order by slide, so it throws
 * on the same corrupt state as `_cframes`: a single-page spread whose slide
 * index outruns the reading order until the pool re-aligns it. Left unguarded
 * the throw escapes whatever animation frame the caller measures in.
 * @param {object} nav - The EpubNavigator instance
 * @returns {{readingOrder: string[], positions: (number|undefined)[]|null}|null}
 */
export function readNavigatorViewport(nav) {
    try {
        return nav?.viewport ?? null;
    } catch {
        // The pool is mid-update. The next call sees the settled spread.
        return null;
    }
}

/**
 * The spine resource a frame serves, or '' when it cannot be read.
 *
 * Readium builds each chapter document in memory and hands the frame a
 * `blob:` object URL, so `location.href` and `document.URL` name nothing the
 * manifest ever mentions. What the frame shows is only recoverable from the
 * pool's own `data-original-href` attribute or the `<base>` R2 injects.
 * @param {Window|null|undefined} frameWindow - A frame's content window
 * @returns {string}
 */
export function readFrameResource(frameWindow) {
    try {
        return frameWindow?.frameElement?.getAttribute('data-original-href') || frameWindow?.document?.baseURI || '';
    } catch {
        // Frame mid-teardown: it serves nothing any more.
        return '';
    }
}
